import { GAME_NAME, TYPE } from "@gaoya/shared";
import { resultHeadline, reasonText } from "./screens/result.js";
import type { UiState } from "../game/state.js";
import { art, C, drawArt, drawPanel, drawSeal, fillRound, roundPath } from "./theme.js";

export const SHARE_W = 600;
export const SHARE_H = 800;

/** 分享战报：600×800，宣纸底 + 朱印 + 战绩 + 联句。 */
export function drawShareCard(ctx: CanvasRenderingContext2D, ui: UiState, w = SHARE_W, h = SHARE_H): void {
  const go = ui.gameOver;
  const win = ui.mode === "RUSH" ? go?.ranking?.find((r) => r.userId === ui.userId)?.rank === 1 : go?.winnerId === ui.userId;
  ctx.fillStyle = C.seal;
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = "rgba(255,226,180,0.08)";
  ctx.beginPath();
  ctx.arc(w * 0.85, h * 0.1, 220, 0, Math.PI * 2);
  ctx.fill();

  // 顶部品牌
  const logo = art("icon");
  if (logo) {
    ctx.save();
    roundPath(ctx, 40, 36, 64, 64, 14);
    ctx.clip();
    ctx.drawImage(logo, 40, 36, 64, 64);
    ctx.restore();
  }
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#FFF7EE";
  ctx.font = `700 30px ${TYPE.serif}`;
  ctx.fillText(GAME_NAME, logo ? 120 : 40, 58);
  ctx.fillStyle = "rgba(255,247,238,0.75)";
  ctx.font = `15px ${TYPE.sans}`;
  ctx.fillText(`${ui.nickname} 的对局战报`, logo ? 120 : 40, 86);

  // 正文卡
  const cx0 = 32;
  const cy0 = 128;
  const cw = w - 64;
  const ch = h - cy0 - 32;
  drawPanel(ctx, cx0, cy0, cw, ch, 24, C.card, null, "rgba(80,16,8,0.35)");
  ctx.save();
  roundPath(ctx, cx0, cy0, cw, ch, 24);
  ctx.clip();
  drawArt(ctx, win ? "char-xia-cut" : "char-scholar-cut", cx0 + cw - 190, cy0 + 4, 180, 180);
  ctx.restore();

  drawSeal(ctx, cx0 + 72, cy0 + 76, 84, win ? "胜" : "战", win ? C.seal : "#6E5E51", "#FFF7EE", -0.06);
  ctx.textAlign = "left";
  ctx.fillStyle = C.ink;
  ctx.font = `700 30px ${TYPE.serif}`;
  ctx.fillText(resultHeadline(ui), cx0 + 32, cy0 + 160);
  ctx.fillStyle = C.ink2;
  ctx.font = `16px ${TYPE.sans}`;
  ctx.fillText(go ? reasonText(go.reason) : "", cx0 + 32, cy0 + 196);

  const stats = go?.stats[ui.userId];
  const cells = [
    ["答对", String(stats?.correct ?? 0)],
    ["失误", String(stats?.mistakes ?? 0)],
    ["超时", String(stats?.timeouts ?? 0)],
    ["最高连击", String(stats?.maxCombo ?? 0)],
  ];
  const sy = cy0 + 226;
  fillRound(ctx, cx0 + 24, sy, cw - 48, 100, 16, C.cardSoft);
  const sw = (cw - 48) / 4;
  ctx.textAlign = "center";
  for (let i = 0; i < cells.length; i++) {
    const c = cells[i];
    if (!c) continue;
    const x = cx0 + 24 + sw * i + sw / 2;
    ctx.fillStyle = i === 0 ? C.seal : C.ink;
    ctx.font = `700 34px ${TYPE.serif}`;
    ctx.fillText(c[1] ?? "0", x, sy + 42);
    ctx.fillStyle = C.ink2;
    ctx.font = `14px ${TYPE.sans}`;
    ctx.fillText(c[0] ?? "", x, sy + 74);
  }

  // 联句
  ctx.textAlign = "left";
  ctx.fillStyle = C.ink;
  ctx.font = `700 18px ${TYPE.serif}`;
  ctx.fillText("接龙记录", cx0 + 32, sy + 136);
  const words = go?.wordChain ?? ui.wordChain;
  ctx.font = `18px ${TYPE.serif}`;
  let x = cx0 + 32;
  let y = sy + 160;
  const maxX = cx0 + cw - 32;
  const chipH = 36;
  const bottom = cy0 + ch - 70;
  for (let i = 0; i < words.length; i++) {
    const word = words[i] ?? "";
    const tw = ctx.measureText(word).width + 24;
    if (x > cx0 + 32 && x + tw > maxX) {
      x = cx0 + 32;
      y += chipH + 10;
    }
    if (y + chipH > bottom) {
      ctx.fillStyle = C.ink3;
      ctx.font = `15px ${TYPE.sans}`;
      ctx.fillText(`…共 ${words.length} 条`, x, y + chipH / 2);
      break;
    }
    const last = i === words.length - 1;
    fillRound(ctx, x, y, tw, chipH, chipH / 2, last ? C.sealSoft : C.cardSoft);
    ctx.fillStyle = last ? C.seal : C.ink;
    ctx.font = `18px ${TYPE.serif}`;
    ctx.fillText(word, x + 12, y + chipH / 2 + 1);
    x += tw + 10;
  }
  if (words.length === 0) {
    ctx.fillStyle = C.ink3;
    ctx.font = `15px ${TYPE.sans}`;
    ctx.fillText("本局暂无联句", cx0 + 32, y + 18);
  }

  ctx.fillStyle = C.line;
  ctx.fillRect(cx0 + 32, cy0 + ch - 56, cw - 64, 1);
  ctx.textAlign = "center";
  ctx.fillStyle = C.ink2;
  ctx.font = `14px ${TYPE.sans}`;
  ctx.fillText("市井茶馆 · 接词大乱斗 · 来和我对一局", w / 2, cy0 + ch - 28);
}
